import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { journalPosts } from '../data/journal';
import { Reveal } from '../components/Reveal';

const formatDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });

const Journal = () => {
  const { lang, t } = useLanguage();

  useEffect(() => {
    document.title = `${t.journal.kicker} — Yoeli Chan Journey`;
    return () => { document.title = 'Yoeli Chan Journey — Your Journey, Your Way'; };
  }, [t]);

  return (
    <div className="bg-ivory" data-testid="journal-page">
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.journal.kicker}</p>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-medium tracking-tight text-navy md:text-6xl">{t.journal.title}</h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/60">{t.journal.sub}</p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {journalPosts.map((post, i) => (
            <Reveal key={post.slug} delay={Math.min(i * 0.06, 0.3)}>
              <Link to={`/journal/${post.slug}`} className="group block" data-testid={`journal-card-${post.slug}`}>
                <div className="overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title[lang]}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/45">
                  <span className="text-gold-dark">{post.category[lang]}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatDate(post.date, lang)}</span>
                  <span aria-hidden="true">·</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readMinutes} {t.journal.minRead}</span>
                </div>
                <h2 className="mt-3 font-heading text-xl leading-snug text-navy transition-colors duration-300 group-hover:text-gold-dark md:text-2xl">
                  {post.title[lang]}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-navy/60">{post.excerpt[lang]}</p>
                <span className="mt-4 inline-flex items-center gap-2 border-b border-gold pb-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold-dark">
                  {t.journal.readMore}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Journal;
